import type { TaxTreatment } from './sales';

// ---------------------------------------------------------------------------
// Purchase orders
// ---------------------------------------------------------------------------

/**
 * Where a purchase order stands.
 *
 * A purchase order is a commitment, never money owed. `DRAFT` is a working document; `ORDERED` is the commitment itself. `PARTIALLY_RECEIVED`, `RECEIVED` and `CLOSED` describe goods arriving and become reachable with goods receipts in Phase 3.4; `CANCELLED` is an order nothing ever arrived against.
 */
export const PurchaseOrderStatus = {
  DRAFT: 'DRAFT',
  ORDERED: 'ORDERED',
  PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
  RECEIVED: 'RECEIVED',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
} as const;
export type PurchaseOrderStatus = (typeof PurchaseOrderStatus)[keyof typeof PurchaseOrderStatus];

/** The statuses an order may still be received against. */
export const RECEIVABLE_PURCHASE_ORDER_STATUSES: readonly PurchaseOrderStatus[] = [
  PurchaseOrderStatus.ORDERED,
  PurchaseOrderStatus.PARTIALLY_RECEIVED,
];

/** The most lines one purchase order may carry: a runaway payload is a denial of service, not an order. */
export const MAX_PURCHASE_ORDER_LINES = 200;
/** The most of one item a single line may order. */
export const MAX_PURCHASE_ORDER_LINE_QUANTITY = 1_000_000;

/** One item on a purchase order, as the document records it. */
export interface PurchaseOrderLineSummary {
  id: string;
  lineNumber: number;
  variantId: string;
  /** Copied when the line is saved and frozen when the order is placed, so a later catalogue edit never rewrites the document. */
  sku: string;
  productName: string;
  productNameAr: string | null;
  variantName: string | null;
  /** Whether goods of this item must name the lot they arrive in. The catalogue's own rule, so receiving never has to guess. */
  isBatchTracked: boolean;
  quantityOrdered: number;
  /** Agreed cost per unit, tax-exclusive, in minor units of the order's currency. */
  agreedUnitCostMinor: number;
  taxProfileId: string | null;
  taxTreatment: TaxTreatment;
  taxRateBasisPoints: number;
  netMinor: number;
  taxMinor: number;
  totalMinor: number;
  /** Counted in by goods receipts, from Phase 3.4. Zero until then. */
  quantityReceived: number;
  /** Cancelled with the order, or closed as a remainder from Phase 3.4. */
  quantityCancelled: number;
  /** `quantityOrdered − quantityReceived − quantityCancelled`: what the supplier still owes. */
  quantityRemaining: number;
  /** Billed by posted supplier invoices against this line's deliveries, from Phase 3.6. Derived on every read, never stored, so voiding an invoice takes its quantity straight back out. */
  quantityInvoiced: number;
  /** `quantityReceived − quantityInvoiced`: delivered goods this line has yet to be billed for. */
  quantityInvoiceable: number;
}

/** A purchase order as a list shows it. */
export interface PurchaseOrderSummary {
  id: string;
  /** PO-000001, allocated when the draft is created. */
  reference: string;
  status: PurchaseOrderStatus;
  /** Moves by one on every change; every write names the version its screen showed. */
  version: number;
  branchId: string;
  branchName: string;
  supplierId: string;
  /** The supplier's names as the order was placed under; while it is a draft they follow the supplier. */
  supplierName: string;
  supplierNameAr: string | null;
  currencyCode: string;
  lineCount: number;
  totalQuantity: number;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  /** The tenant's business date the order was placed on. */
  orderedOn: string | null;
  expectedDeliveryDate: string | null;
  supplierReference: string | null;
  orderedByName: string | null;
  cancelledAt: string | null;
  /** Set when what the supplier still owed was given up on. */
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A purchase order with its lines. */
export interface PurchaseOrderDetail extends PurchaseOrderSummary {
  notes: string | null;
  cancelledReason: string | null;
  cancelledByName: string | null;
  closedReason: string | null;
  closedByName: string | null;
  createdByName: string | null;
  /** True while the supplier can still be chosen for new orders; a draft cannot be placed when it is false. */
  supplierIsActive: boolean;
  lines: PurchaseOrderLineSummary[];
}

// ---------------------------------------------------------------------------
// Goods receipts
// ---------------------------------------------------------------------------

/** The most lines one goods receipt may carry. A line is per lot, so this is generous by design. */
export const MAX_PURCHASE_RECEIPT_LINES = 400;

/** One lot of one ordered item arriving on one delivery. */
export interface PurchaseReceiptLineSummary {
  id: string;
  lineNumber: number;
  purchaseOrderLineId: string;
  variantId: string;
  /** From the purchase order line, which froze them when the order was placed. */
  sku: string;
  productName: string;
  productNameAr: string | null;
  variantName: string | null;
  quantityReceived: number;
  /** The lot these goods went into. Null for an item without lot tracking. */
  batchId: string | null;
  lotNumber: string | null;
  expiryDate: string | null;
  /** The order's agreed cost, copied so the delivery keeps its value. */
  agreedUnitCostMinor: number;
  taxTreatment: TaxTreatment;
  taxRateBasisPoints: number;
  netMinor: number;
  taxMinor: number;
  totalMinor: number;
  /** The one ledger row this line put on the shelf. */
  stockTransactionId: string;
  /** Billed by posted supplier invoices, from Phase 3.6. Derived on every read, never stored. */
  quantityInvoiced: number;
  /** `quantityReceived − quantityInvoiced`: what is left to bill on this line. */
  quantityInvoiceable: number;
}

/** A goods receipt as a list shows it. */
export interface PurchaseReceiptSummary {
  id: string;
  /** GRN-000001, allocated when the receipt is posted. */
  reference: string;
  purchaseOrderId: string;
  purchaseOrderReference: string;
  supplierId: string;
  supplierName: string;
  supplierNameAr: string | null;
  branchId: string;
  branchName: string;
  currencyCode: string;
  /** The tenant's business date the goods were counted in. */
  receivedOn: string;
  supplierDeliveryReference: string | null;
  lineCount: number;
  totalQuantity: number;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  receivedByName: string | null;
  createdAt: string;
}

/** A goods receipt with its lines. Posted receipts never change, so there is no version. */
export interface PurchaseReceiptDetail extends PurchaseReceiptSummary {
  lines: PurchaseReceiptLineSummary[];
}

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------

/**
 * A tax profile as purchasing needs to see it.
 *
 * The tenant's own reference data, not the sales module's: a buyer raising a purchase order must be
 * able to say which tax an item is bought under without being granted anything to do with selling.
 *
 * Deliberately narrower than the sales view. `pricesIncludeTax` is left out because a purchase cost
 * is always agreed net of tax, so the flag would only invite a reader to apply it; inactive profiles
 * are not returned at all, so there is no flag for that either.
 */
export interface PurchasingTaxProfile {
  id: string;
  code: string;
  nameEn: string;
  nameAr: string | null;
  rateBasisPoints: number;
  treatment: TaxTreatment;
  /** The one a new line starts on when nothing else is chosen. */
  isDefault: boolean;
}

// ---------------------------------------------------------------------------
// Supplier invoices
// ---------------------------------------------------------------------------

/**
 * Where a supplier invoice stands.
 *
 * There is no draft: entering one records a document the supplier already sent. `VOIDED` is the one
 * transition, and it is not a deletion — the document and its number stay, only the obligation goes.
 */
export const PurchaseInvoiceStatus = {
  POSTED: 'POSTED',
  VOIDED: 'VOIDED',
} as const;
export type PurchaseInvoiceStatus =
  (typeof PurchaseInvoiceStatus)[keyof typeof PurchaseInvoiceStatus];

/** The most lines one supplier invoice may carry. */
export const MAX_PURCHASE_INVOICE_LINES = 200;

/**
 * A delivered line with something still to bill, and the context needed to recognise it.
 *
 * Everything here is derived: the receipt line is immutable, and what has already been billed is
 * the sum of the posted invoices against it. Nothing is stored as a counter.
 */
export interface InvoiceableReceiptLine {
  purchaseReceiptLineId: string;
  purchaseReceiptId: string;
  receiptReference: string;
  receivedOn: string;
  purchaseOrderId: string;
  purchaseOrderReference: string;
  supplierId: string;
  supplierName: string;
  branchId: string;
  branchName: string;
  currencyCode: string;
  variantId: string;
  sku: string;
  productName: string;
  productNameAr: string | null;
  variantName: string | null;
  lotNumber: string | null;
  quantityReceived: number;
  /** Counted from posted invoices only: a voided one bills nothing. */
  quantityInvoiced: number;
  quantityInvoiceable: number;
  /** What the order agreed. The supplier may still bill something else. */
  agreedUnitCostMinor: number;
  taxProfileId: string | null;
  taxTreatment: TaxTreatment;
  taxRateBasisPoints: number;
}

/** One delivered line as an invoice bills it. */
export interface PurchaseInvoiceLineSummary {
  id: string;
  lineNumber: number;
  purchaseReceiptLineId: string;
  receiptReference: string;
  purchaseOrderReference: string;
  variantId: string;
  sku: string;
  productName: string;
  productNameAr: string | null;
  variantName: string | null;
  quantityReceived: number;
  quantityInvoiced: number;
  /** The order's cost, kept beside the billed one so the difference is readable. */
  agreedUnitCostMinor: number;
  billedUnitCostMinor: number;
  /** `billedUnitCostMinor − agreedUnitCostMinor`: positive when the supplier billed more than agreed. */
  unitCostVarianceMinor: number;
  taxProfileId: string | null;
  taxTreatment: TaxTreatment;
  taxRateBasisPoints: number;
  netMinor: number;
  taxMinor: number;
  totalMinor: number;
}

/** A supplier invoice as a list shows it. */
export interface PurchaseInvoiceSummary {
  id: string;
  /** PINV-000001, allocated when the invoice is posted. */
  reference: string;
  /** The number printed on the supplier's own document. */
  supplierInvoiceNumber: string;
  status: PurchaseInvoiceStatus;
  supplierId: string;
  supplierName: string;
  supplierNameAr: string | null;
  branchId: string;
  branchName: string;
  currencyCode: string;
  invoiceDate: string;
  dueDate: string;
  paymentTermsDays: number;
  lineCount: number;
  totalQuantity: number;
  netMinor: number;
  taxMinor: number;
  totalMinor: number;
  /** What this invoice contributes to what the supplier is owed: its total while posted, nothing once voided. */
  obligationMinor: number;
  postedByName: string | null;
  postedAt: string;
  voidedAt: string | null;
}

/** A supplier invoice with its lines. */
export interface PurchaseInvoiceDetail extends PurchaseInvoiceSummary {
  voidedReason: string | null;
  voidedByName: string | null;
  lines: PurchaseInvoiceLineSummary[];
}
